import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Checkbox,
  Paper,
} from "@mui/material";
import { Grid, Stack } from "@mui/system";
import SaveIcon from "@mui/icons-material/Save";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CheckIcon from "@mui/icons-material/Check";
import { useSelector } from "react-redux";
import { useState } from "react";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import { setSelectedRows } from "../../../../../../features/actionItemsSlice";
import { useUpdateAcionItemsStatus } from "../../../../../../customHooks/useUpdateAcionItemsStatus";
import { validateDateFormat } from "../../../../../../utils/actionItemsValidation";

const SnoozePopup = ({
  open,
  handleClose,
}: {
  open: boolean;
  handleClose: () => void;
}) => {
  const [comment, setComment] = useState<string>("");
  const { loading, updateActionItemsStatus } =
    useUpdateAcionItemsStatus("snoozed"); // Custom hook for updating status(snoozed)
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const { selectedRows } = useSelector((state: any) => state?.actionItems);

  const [isshowMsg, setIsShowMsg] = useState(false);
  const dispatch = useDispatch();
  //-- Initialize all selected rows
  const [selected, setSelected] = useState<any[]>(selectedRows);

  //-- Check if all rows are selected
  const isAllSelected = selected.length === selectedRows.length;

  //-- Handle individual row selection
  const handleSelectRow = (row: any) => {
    setSelected((prev) =>
      prev.some((r) => r === row)
        ? prev.filter((r) => r !== row)
        : [...prev, row]
    );
  };

  //-- Handle "Select All" checkbox
  const handleSelectAll = () => {
    setSelected(isAllSelected ? [] : [...selectedRows]);
  };

  //-- Check if the button is disabled or not
  const isButtonDisabled = () => {
    if (selected.length === 0 || !selectedDate || isshowMsg) return true;
    if (selected.length > 1 && (!comment || !selectedDate)) return true;
    return false;
  };

  const checkIsLessDueDate = (snoozeDate: any) => {
    if (validateDateFormat(snoozeDate)) return;
    setIsShowMsg(false);
    //-- If due_date <= snooze_end date then show error message according to docs
    const invalidTasks = selected.some(
      (row) =>
        dayjs(row?.due_date).format("YYYY-MM-DD") <=
        dayjs(snoozeDate).format("YYYY-MM-DD")
    );

    if (invalidTasks) {
      setIsShowMsg(true);
      //return;
    }
    if (!invalidTasks) {
      setIsShowMsg(false);
    }
  };

  //-- Request body for updating task status Snooze
  const snoozeBodyActionItem = {
    tasks: selected.map((task) => ({
      shared_uid: task.shared_uid,
      current_status: "Snooze",
      snooze_end: selectedDate,
    })),
  };

  //-- Close the Snooze modal and clear the selected rows(unchecked checkboxes)
  const handleCloseSnoozeModal = () => {
    handleClose(); // Close modal if success
    dispatch(setSelectedRows([])); // Clear selected rows
  };

  //-- Clicking the Snooze button to update the status
  const handleActions = async () => {
    if (!selectedDate || selected.length === 0) return;
    //-- this function for update status(MAKE API call for update status)
    updateActionItemsStatus(
      snoozeBodyActionItem,
      comment,
      handleCloseSnoozeModal
    );
    setIsShowMsg(false); // Clear error message
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        // Prevent close if loading and the reason is backdrop click or escape key
        if (
          loading &&
          (reason === "backdropClick" || reason === "escapeKeyDown")
        ) {
          return;
        }
        handleCloseSnoozeModal();
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{}}>Snooze Tasks</DialogTitle>
      <DialogContent>
        <Typography gutterBottom sx={{ paddingBottom: "8px" }}>
          Selected Tasks
        </Typography>
        <Paper
          sx={{
            width: "100%",
            overflowY: "auto",
            height: "auto",
            maxHeight: "225px",
            scrollbarWidth: "thin",
          }}
        >
          <Table size="small" aria-label="a dense table">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ padding: "4px" }}>
                  <Checkbox
                    size="small"
                    color="secondary"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ padding: "4px" }}>Item Name</TableCell>
                <TableCell sx={{ padding: "4px" }}>Target</TableCell>
                <TableCell sx={{ padding: "4px" }}>Due Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedRows.map((row: any, index: number) => (
                <TableRow
                  key={index}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell padding="checkbox" sx={{ padding: "4px" }}>
                    <Checkbox
                      size="small"
                      color="secondary"
                      checked={selected.some((r) => r === row)}
                      onChange={() => handleSelectRow(row)}
                    />
                  </TableCell>
                  <TableCell sx={{ padding: "4px" }}>
                    {row?.data["action_item"]}
                  </TableCell>
                  <TableCell sx={{ padding: "4px" }}>
                    {row?.data["target"]}
                  </TableCell>
                  <TableCell align="left" sx={{ padding: "4px" }}>
                    {row?.due_date && dayjs(row?.due_date).format("DD/MM/YYYY")}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
        <Typography gutterBottom marginBlock={2}>
          Snooze details
        </Typography>
        <Grid container spacing={2}>
          <Grid size={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                className="brandDataDatePicker"
                slotProps={{
                  textField: {
                    size: "small",
                    color: "secondary",
                    required: true,
                    fullWidth: true,
                    helperText: "These fields are mandatory",
                    placeholder: "Pick a date",
                    InputLabelProps: { shrink: true },
                    inputProps: {
                      readOnly: true,
                      onKeyDown: (e) => {
                        // Block ALL keys including numbers and delete
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                      },
                      onKeyPress: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                      },
                      onKeyUp: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                      },
                      onInput: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                      },
                      onBeforeInput: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                      },
                      onPaste: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                      },
                      onCut: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                      },
                      onChange: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                      },
                      onFocus: (e) => {
                        e.target.blur(); // Remove focus immediately
                      },
                      style: {
                        pointerEvents: "none",
                        caretColor: "transparent",
                        userSelect: "none",
                        WebkitUserSelect: "none",
                        MozUserSelect: "none",
                        cursor: "default",
                      },
                    },
                    sx: {
                      "& .MuiInputBase-input": {
                        pointerEvents: "none !important",
                        caretColor: "transparent !important",
                        userSelect: "none !important",
                        WebkitUserSelect: "none !important",
                        MozUserSelect: "none !important",
                        "&:focus": {
                          outline: "none !important",
                          boxShadow: "none !important",
                        },
                      },
                      "& .MuiIconButton-root": {
                        pointerEvents: "auto !important",
                        cursor: "pointer !important",
                      },
                    },
                  },
                }}
                format="DD/MM/YYYY"
                minDate={dayjs()}
                disablePast
                label="Snooze until"
                onChange={(date) => {
                  if (date) {
                    setSelectedDate(dayjs(date).format("YYYY-MM-DD HH:mm:ss"));
                    checkIsLessDueDate(dayjs(date).format("YYYY-MM-DD"));
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid size={6}>
            <TextField
              fullWidth
              label="Reason"
              size="small"
              color="secondary"
              required={selected?.length > 1}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Why item is snoozed"
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ padding: "20px" }}>
        {isshowMsg && (
          <Typography color="error">
            One or more actions can’t be snoozed after their Due date. Please
            adjust Due date or Snooze date accordingly.
          </Typography>
        )}
        <Button
          onClick={() => {
            handleClose();
            dispatch(setSelectedRows([]));
          }}
          color="secondary"
          disabled={loading} //disable Cancel button during loading
        >
          Cancel
        </Button>
        <Button
          data-tracker="120"
          variant="contained"
          color="secondary"
          loading={loading}
          loadingPosition="end"
          endIcon={loading && <SaveIcon />}
          disabled={isButtonDisabled()}
          onClick={() => handleActions()}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            SNOOZE
            <CheckIcon fontSize="small" />
          </Stack>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SnoozePopup;
